import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';

const MetricCard = ({ title, value, subtitle, color = '#1DA1F2' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-2">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        <div className="text-white text-[18px] font-medium mb-1">{value}</div>
        {subtitle && (
            <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
        )}
    </div>
);

const ContentCard = ({ title, children }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-4">
            <div className="w-[8px] h-[8px] rounded-full bg-[#1DA1F2]" />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        {children}
    </div>
);

const SocialListeningAnalytics = () => {
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('week');
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadAnalytics();
    }, [filter]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await getDataAPI(`user/analytics/social-listening?filter=${filter}`, token);
            setAnalyticsData(response.data.data.response);
        } catch (error) {
            console.error('Failed to load social listening analytics:', error);
            setAnalyticsData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (!num || isNaN(num)) return '0';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return new Intl.NumberFormat('en-US').format(num);
    };

    const getSentimentColor = (sentiment) => {
        switch (sentiment?.toLowerCase()) {
            case 'positive': return '#22C55E';
            case 'negative': return '#EF4444';
            case 'neutral': return '#6B7280';
            default: return '#1DA1F2';
        }
    };

    const getSentimentIcon = (sentiment) => {
        switch (sentiment?.toLowerCase()) {
            case 'positive': return '😊';
            case 'negative': return '😞';
            case 'neutral': return '😐';
            default: return '📊';
        }
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
                    <p className="text-[12px]">Loading social listening analytics...</p>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] h-screen bg-black text-white relative">
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <div className="flex items-center gap-2">
                            <div 
                                className="w-[24px] h-[24px] rounded-md flex items-center justify-center text-[8px] font-medium text-white"
                                style={{ backgroundColor: '#1DA1F2' }}
                            >
                                SL
                            </div>
                            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Social Listening Analytics</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className='text-[red] cursor-pointer text-[12px]'>Export data</p>
                            <div 
                                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                                onClick={() => navigate('/analytics/hub')}
                            >
                                Back
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
                        {/* Filter */}
                        <div className="mb-6">
                            <select 
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="bg-[#1A1A1A] border border-[#333] text-white text-[12px] px-3 py-2 rounded"
                            >
                                <option value="day">Last 24 Hours</option>
                                <option value="week">Last Week</option>
                                <option value="month">Last Month</option>
                                <option value="year">Last Year</option>
                                <option value="all">All Time</option>
                            </select>
                        </div>

                        {analyticsData ? (
                            <div className="space-y-4">
                                {/* Top Metrics Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="Total Mentions"
                                        value={formatNumber(analyticsData.totalMentions || 0)}
                                        subtitle="Across all platforms"
                                    />
                                    <MetricCard
                                        title="Reach"
                                        value={formatNumber(analyticsData.totalReach || 0)}
                                        subtitle="Potential audience"
                                    />
                                </div>

                                {/* Second Metrics Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="Engagement Rate"
                                        value={`${analyticsData.engagementRate || 0}%`}
                                        subtitle="Avg engagement"
                                    />
                                    <MetricCard
                                        title="Sentiment Score"
                                        value={analyticsData.sentimentScore || '--'}
                                        subtitle="Overall sentiment"
                                        color={getSentimentColor(analyticsData.overallSentiment)}
                                    />
                                </div>

                                {/* Platform Breakdown */}
                                <ContentCard title="Platform Breakdown">
                                    <div className="space-y-3">
                                        {analyticsData.platformBreakdown && analyticsData.platformBreakdown.length > 0 ? (
                                            analyticsData.platformBreakdown.map((platform, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-light">{platform.name}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                            {formatNumber(platform.reach)} reach
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[#1DA1F2] text-[12px] font-medium">{formatNumber(platform.mentions)}</div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">mentions</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No platform data available</p>
                                        )}
                                    </div>
                                </ContentCard>

                                {/* Trending Mentions */}
                                <ContentCard title="Trending Mentions">
                                    <div className="space-y-3">
                                        {analyticsData.trendingMentions && analyticsData.trendingMentions.length > 0 ? (
                                            analyticsData.trendingMentions.map((mention, index) => (
                                                <div key={index} className="flex items-start gap-3 py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-medium">{mention.content}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px] mt-1">
                                                            @{mention.author} • {mention.platform} • {mention.timeAgo}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[12px]">{getSentimentIcon(mention.sentiment)}</span>
                                                        <div className="text-right">
                                                            <div className="text-[#1DA1F2] text-[10px] font-bold">{formatNumber(mention.engagement)}</div>
                                                            <div className="text-[rgba(255,255,255,0.5)] text-[9px]">engagement</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No trending mentions</p>
                                        )}
                                    </div>
                                </ContentCard>

                                {/* Sentiment Analysis */}
                                <ContentCard title="Sentiment Analysis">
                                    <div className="space-y-3">
                                        {analyticsData.sentimentBreakdown ? (
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between py-1">
                                                    <div className="flex items-center gap-2">
                                                        <span>😊</span>
                                                        <span className="text-white text-[12px]">Positive</span>
                                                    </div>
                                                    <div className="text-[#22C55E] text-[12px] font-bold">
                                                        {analyticsData.sentimentBreakdown.positive || 0}%
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between py-1">
                                                    <div className="flex items-center gap-2">
                                                        <span>😐</span>
                                                        <span className="text-white text-[12px]">Neutral</span>
                                                    </div>
                                                    <div className="text-[#6B7280] text-[12px] font-bold">
                                                        {analyticsData.sentimentBreakdown.neutral || 0}%
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between py-1">
                                                    <div className="flex items-center gap-2">
                                                        <span>😞</span>
                                                        <span className="text-white text-[12px]">Negative</span>
                                                    </div>
                                                    <div className="text-[#EF4444] text-[12px] font-bold">
                                                        {analyticsData.sentimentBreakdown.negative || 0}%
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No sentiment data available</p>
                                        )}
                                    </div>
                                </ContentCard>
                            </div>
                        ) : (
                            <div className="text-center py-10">
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No social listening data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default SocialListeningAnalytics; 